import { Component, OnDestroy, OnInit } from '@angular/core';
import { AreaApiService } from './area.service';
import { DictionaryService } from 'src/app/services/dictionary.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SelectOption, SnackbarService } from '@bcl/design-system';
import {
  Area,
  Detection,
  LocationDetailsOptions,
  Monitor,
  MonitorWithOptions,
} from './area.model';
import {
  combineLatest,
  map,
  Observable,
  Subject,
  Subscription,
  takeUntil,
  tap,
} from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import {
  DetectionDialogComponent,
  DetectionDialogData,
} from './components/detection-dialog/detection-dialog.component';
import { ScannerService } from 'src/app/services/scanner.service';
import { ObservationMonitorDetails } from './components/area-details-monitors/area-details-monitors.component';
import { VisitService } from 'src/app/services/visit.service';
import { Platform } from '@ionic/angular';
import { DataProviderService } from 'src/app/services/data-provider.service';
import { BackButtonService } from 'src/app/services/back-button.service';
import {
  ActionApiService,
  CreateActionDto,
} from 'src/app/pages/action/action.service';
import { ActionsList, DateRange } from 'src/app/pages/action/action.model';
import { ActionsListPageChanged } from 'src/app/shared-components/components/actions-list/actions.model';
import { ParentObject } from 'src/app/shared-components/components/notes-list/notes.model';
import { StateService } from 'src/app/services/state.service';
import { ObjectPlacerCoordinatesSelectorData } from '../../components';
import {
  AreaMapObjectsService,
  mapMonitorsToMapObjects,
} from '../area-map/area-map-objects';

@Component({
  selector: 'app-area',
  templateUrl: './area.component.html',
  styleUrls: ['./area.component.scss'],
  providers: [AreaMapObjectsService],
})
export class AreaPage implements OnInit, OnDestroy {
  public loading = true;
  public visitId: number;
  public date: string;
  public dayNumber: string;
  public areaId: number;
  public selectedArea?: Area;
  public scannedValue: string;
  public mappedMonitors: Array<MonitorWithOptions> = [];
  public mappedActionsMonitors: Array<MonitorWithOptions> = [];
  public visit?: any; // Add visit property
  public options: LocationDetailsOptions = {
    location: {
      technicians: [],
      skills: [],
      evaluationQuestions: [],
      days: [],
    },
    area: { types: [], subtypes: [], certifications: [] },
    monitor: {
      modules: [],
      types: [],
      statuses: [],
      product: { products: [], units: [], warehouses: [] },
      groups: [],
    },
  };
  public detectionSources: SelectOption[] = [];
  public detectionTypes: SelectOption[] = [];
  public instrumentTypes: SelectOption[] = [];
  public detections: Detection[] = [];
  public isEditingDisabled = false;
  public actionsList: ActionsList = { actions: [], totalEntries: 0 };
  public dateFilterOptions: SelectOption[] = [];
  public statusFilterOptions: SelectOption[] = [];
  public actionsShowItemList = true;
  private destroy$ = new Subject<void>();
  private backButtonSubscription: Subscription;
  private allVisitsForDay: any[] = [];
  private lastDateFilter: DateRange = '4';
  private newActionAreaMapData: ObjectPlacerCoordinatesSelectorData | undefined;

  constructor(
    private areaApiService: AreaApiService,
    private dictionaryService: DictionaryService,
    private route: ActivatedRoute,
    private snackbarService: SnackbarService,
    private router: Router,
    public dialog: MatDialog,
    private scannerService: ScannerService,
    private visitService: VisitService,
    private platform: Platform,
    private dataService: DataProviderService,
    private backButtonService: BackButtonService,
    private actionService: ActionApiService,
    private stateService: StateService,
    private areaMapObjectsService: AreaMapObjectsService
  ) {}

  // Computed property to check if form should be disabled
  get isFormDisabled(): boolean {
    return this.isEditingDisabled;
  }

  public ngOnInit(): void {
    this.backButtonService.showBackButton({
      show: true,
      action: () => {
        this.goBack();
      },
    });

    this.scannerService
      .getScanResultObservable('area')
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.scannedValue = result;
      });
    this.backButtonSubscription =
      this.platform.backButton.subscribeWithPriority(10, () => {
        this.dialog.closeAll();
        this.goBack();
      });
    this.route.params.subscribe((params) => {
      this.visitId = params?.id;
      this.date = params?.date;
      this.dayNumber = params?.dayId;
      this.areaId = params?.areaid;
      combineLatest({
        area: this.areaApiService.getArea(this.areaId, this.visitId),
        detections: this.areaApiService.getDetections(this.areaId),
        areaMonitors: this.areaApiService.getMonitors(
          this.areaId,
          this.visitId,
          {
            status: null,
            module: null,
            type: null,
          }
        ),
        options: this.areaApiService.getLocationDetailsOptions(this.areaId),
        groups: this.dictionaryService
          .getDictionaryItemsByKey('PC.INSTRUMENT.TYPE')
          .pipe(
            map((items) =>
              items.map((item) => ({
                ...item,
                additionalValues: item.additionalValues
                  ? JSON.parse(item.additionalValues)
                  : null,
              }))
            )
          ),
        areaTypes:
          this.dictionaryService.getDictionaryItemsByKey('PC.AREA.TYPE'),
        detectionSource: this.dictionaryService.getDictionaryItemsByKey(
          'PC.DETECTIONSOURCE.TYPE'
        ),
        detectionType:
          this.dictionaryService.getDictionaryItemsByKey('PC.DETECTION.TYPE'),
        instrumentType: this.dictionaryService.getDictionaryItemsByKeyV2(
          'TABLE.PC.INSTRUMENTTYPES'
        ),
        visit: this.dataService.getEljakimJob(this.visitId.toString()),
        allVisitsForDay: this.dataService.getEljakimJobs(this.date),
        dateFilters: this.dictionaryService.getDateFilters(),
        statusFilterOptions: this.dictionaryService.getActionStatuses(),
      }).subscribe(
        ({
          area,
          detections,
          areaMonitors,
          options,
          groups,
          detectionSource,
          detectionType,
          instrumentType,
          visit,
          allVisitsForDay,
          dateFilters,
          statusFilterOptions,
        }) => {
          this.selectedArea = area;
          this.detections = detections;
          this.selectedArea.monitors = areaMonitors;
          this.visit = visit;
          this.allVisitsForDay = allVisitsForDay;

          // Update disabled states
          this.updateDisabledStates();

          this.selectedArea.typeName =
            options.area.types.find((t) => t.value === area.typeId)?.label ??
            '';
          this.options = {
            ...options,
            monitor: {
              ...options.monitor,
              groups,
            },
          };
          this.mappedMonitors = this.mapMonitors(areaMonitors).filter(
            (monitor) => monitor.type === 'Monitor'
          );
          this.mappedActionsMonitors = this.mapMonitors(areaMonitors);
          if (this.scannedValue) {
            const monitor = this.mappedMonitors.find(
              (mon) => mon.qrCode === this.scannedValue
            );

            if (monitor) {
              this.router.navigate([
                '/area/monitor',
                this.date,
                this.visitId,
                this.dayNumber,
                this.areaId,
                monitor.id,
              ]);
            } else {
              this.snackbarService.open('red', 'MONITOR_NOT_FOUND');
            }
          }
          this.detectionSources = detectionSource.map((source) => ({
            value: source.value,
            label: source.name,
          }));
          this.detectionTypes = detectionType.map((type) => ({
            value: type.value,
            label: type.name,
          }));
          this.instrumentTypes = instrumentType.Data.map((type) => ({
            value: type.Id?.toString() || '',
            label: type.Name?.toString() || '',
          }));

          // Initialize filter options for actions from API
          this.dateFilterOptions = dateFilters.map((x) => ({
            value: x.value,
            label: x.name,
          }));
          this.statusFilterOptions = statusFilterOptions.map((x) => ({
            value: x.value,
            label: x.name,
          }));

          // Load initial actions
          this.getActions('4', '0', 1, 10);

          const areaMapMonitors = mapMonitorsToMapObjects(
            this.mappedActionsMonitors
          );
          this.newActionAreaMapData = {
            objects: areaMapMonitors,
            objectSvg: this.areaMapObjectsService.buildMonitorsSvg(
              areaMapMonitors,
              this.options
            ).monitorSvg,
            backgroundSource: this.selectedArea?.map,
          };

          this.loading = false;
        },
        () => {
          this.loading = false;
          this.snackbarService.open(
            'red',
            'GENERIC_ERROR_MESSAGES.AN_ERROR_OCCURRED_UNABLE_TO_LOAD_DATA'
          );
        }
      );
    });
  }

  ionViewWillEnter(): void {
    this.backButtonService.showBackButton({
      show: true,
      action: () => {
        this.goBack();
      },
    });

    if (this.areaId && this.visitId && this.selectedArea) {
      combineLatest({
        monitors: this.refreshMonitors(),
        detections: this.areaApiService.getDetections(this.areaId),
      })
        .pipe(takeUntil(this.destroy$))
        .subscribe(({ detections }) => {
          this.detections = detections;
          this.actionsList = { actions: [], totalEntries: 0 };
          this.getActions(this.lastDateFilter, '0', 1, 10);
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.backButtonSubscription?.unsubscribe();
  }

  goToMap(): void {
    this.saveFields();
    this.router.navigate([
      'area/map/',
      this.date,
      this.visitId,
      this.dayNumber,
      this.areaId,
    ]);
  }

  public addDetection(): void {
    const dialogRef = this.dialog.open<
      DetectionDialogComponent,
      DetectionDialogData
    >(DetectionDialogComponent, {
      disableClose: true,
      data: {
        detectionSources: this.detectionSources,
        detectionTypes: this.detectionTypes,
        areaMapData: this.newActionAreaMapData,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.areaApiService
          .createDetection(
            this.visitId,
            this.areaId,
            result.detection.detectionSourceId,
            result.detection.detectionTypeId,
            result.detection.detectionCount,
            result.detection.memo
          )
          .subscribe({
            next: (detectionResponse) => {
              this.snackbarService.open(
                'green',
                'DETECTION_EDIT.MESSAGES.DETECTION_CREATED'
              );

              // If action data is provided, create the action
              if (result.action) {
                const actionData: CreateActionDto = {
                  parentObject: 'PC.Detection',
                  parentID: detectionResponse.id,
                  ['number']: '',
                  actionTypeID: result.action.actionTypeId,
                  statusID: '0',
                  responsibilityID: undefined,
                  deadline: result.action.deadline,
                  deadlineTime: undefined,
                  duration: undefined,
                  title: result.action.title,
                  description: result.action.description,
                  assigneeID: this.stateService.getValue('userId'),
                  extended: true,
                  customerID: this.selectedArea.customerId,
                  locationID: this.selectedArea.locationId,
                  areaID: this.areaId,
                  contactId: undefined,
                  initialApprovalBy: undefined,
                  initialApprovalDate: undefined,
                  finalApprovalBy: undefined,
                  finalApprovalDate: undefined,
                  reason: undefined,
                  priorityId: '0',
                  mapX: result.action.mapCoordinates.x,
                  mapY: result.action.mapCoordinates.y,
                };
                this.actionService.createAction(actionData).subscribe({
                  next: (actionResponse: any) => {
                    this.snackbarService.open(
                      'green',
                      'DETECTION_EDIT.MESSAGES.ACTION_CREATED'
                    );

                    const actionId =
                      actionResponse?.id ||
                      actionResponse?.data?.id ||
                      actionResponse;

                    combineLatest({
                      monitors: this.refreshMonitors(),
                      detections: this.areaApiService.getDetections(
                        this.areaId
                      ),
                    })
                      .pipe(takeUntil(this.destroy$))
                      .subscribe(() => {
                        this.actionsList = { actions: [], totalEntries: 0 };
                        this.getActions(this.lastDateFilter, '0', 1, 10);

                        if (actionId) {
                          setTimeout(() => {
                            this.router.navigate(['action'], {
                              state: {
                                backUrl: `/area/${this.date}/${this.visitId}/${this.dayNumber}/${this.areaId}`,
                                parentObject: 'PC.Detection',
                                parentId: detectionResponse.id,
                                customerId: this.selectedArea.customerId,
                                locationId: this.selectedArea.locationId,
                                areaId: this.areaId,
                                actionId,
                              },
                            });
                          }, 100);
                        }
                      });
                  },
                  error: (error) => {
                    this.snackbarService.open(
                      'red',
                      'DETECTION_EDIT.MESSAGES.ACTION_CREATE_ERROR'
                    );
                  },
                });
              } else {
                // Refresh detections list
                this.areaApiService
                  .getDetections(this.areaId)
                  .subscribe((detections) => {
                    this.detections = detections;
                  });
              }
            },
            error: () => {
              this.snackbarService.open(
                'red',
                'DETECTION_EDIT.MESSAGES.DETECTION_CREATE_ERROR'
              );
            },
          });
      }
    });
  }

  public observationChange(
    observationMonitorDetails: ObservationMonitorDetails
  ): void {
    if (observationMonitorDetails?.id) {
      this.areaApiService
        .changeMonitorObservation(
          observationMonitorDetails.monitorId,
          this.visitId,
          { id: observationMonitorDetails.id, details: '' }
        )
        .subscribe({
          next: () => {
            this.snackbarService.open(
              'green',
              'MONITOR.DETAILS.MESSAGES.OBSERVATION_CHANGED'
            );
            const monitor = this.mappedMonitors.find(
              (m) => m.id === observationMonitorDetails.monitorId
            );
            if (monitor) {
              monitor.observation.id = 2;
              monitor.observation.name = 'Ok';
            }
          },
          error: () => {
            this.snackbarService.open(
              'red',
              'MONITOR.DETAILS.MESSAGES.OBSERVATION_CHANGE_ERROR'
            );
          },
        });
    }
  }

  public saveFields(): void {
    // Only save if visit is not completed and summary is not empty
    if (
      !this.isFormDisabled &&
      this.selectedArea.summary &&
      this.selectedArea.summary.trim() !== ''
    ) {
      this.visitService
        .updateField(
          'PestControl.Areas',
          this.areaId,
          this.selectedArea.summary,
          'Summary',
          this.visitId
        )
        .subscribe({
          next: () => {},
          error: () => {},
        });
    }
  }

  public handleMonitorsRefresh(monitors: Monitor[]): void {
    this.selectedArea.monitors = monitors;
    this.mappedMonitors = this.mapMonitors(monitors).filter(
      (monitor) => monitor.type === 'Monitor'
    );
  }

  public loadMoreActions(page: ActionsListPageChanged): void {
    this.lastDateFilter = page.filter.date ?? '4';
    this.getActions(
      this.lastDateFilter,
      page.filter.status ?? '0',
      page.pageNumber,
      page.pageSize
    );
  }

  public getActions(
    dateFilterString: DateRange,
    statusID: string,
    pageNumber: number,
    pageSize: number
  ): void {
    this.actionService
      .getActions({
        parentID: this.areaId,
        parentObject: 'PC.Area' as ParentObject,
        dateFilter: dateFilterString,
        statusID,
        pageNumber,
        pageSize,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.actionsList = result;
        },
        error: () => {
          this.snackbarService.open(
            'red',
            'GENERIC_ERROR_MESSAGES.AN_ERROR_OCCURRED_UNABLE_TO_LOAD_DATA'
          );
        },
      });
  }

  public goBack() {
    this.dialog.closeAll();
    this.saveFields();
    this.router.navigate([
      '/visitday/details',
      this.date,
      this.visitId,
      this.dayNumber,
    ]);
  }

  public goToHome(): void {
    this.router.navigate(['/home']);
  }

  /**
   * Update the edit disabled state based on current visit and other visits
   */
  public updateDisabledStates(): void {
    this.isEditingDisabled = this.visitService.isEditingDisabled(this.visit);
  }

  public refreshMonitors(): Observable<Monitor[]> {
    return this.areaApiService
      .getMonitors(this.areaId, this.visitId, {
        status: null,
        module: null,
        type: null,
      })
      .pipe(
        tap((areaMonitors) => {
          if (this.selectedArea) {
            this.selectedArea.monitors = areaMonitors;
          }
          this.mappedMonitors = this.mapMonitors(areaMonitors).filter(
            (monitor) => monitor.type === 'Monitor'
          );
          this.mappedActionsMonitors = this.mapMonitors(areaMonitors);
        })
      );
  }

  private mapMonitors(monitors: Array<Monitor>): Array<MonitorWithOptions> {
    return monitors.map((monitor) => {
      const status = this.options.monitor.statuses.find(
        (s) => s.value === monitor.statusId
      );
      return {
        ...monitor,
        moduleName:
          this.options.monitor.modules.find((m) => m.value === monitor.moduleId)
            ?.label ?? '',
        typeName:
          this.options.monitor.types.find((t) => t.value === monitor.typeId)
            ?.label ?? '',
        status: {
          name: status?.label ?? '',
          style: status?.style,
          statusId: monitor.statusId,
        },
        batteryStatus: monitor.batteryStatus
          ? +(monitor.batteryStatus * 100).toFixed(2)
          : undefined,
      };
    });
  }
}
